import { detectBallsTiled, loadBallDetectorModel, makeWorkCanvas, pickBestCandidate } from "./ballDetector";
import {
  extrapolateLanding,
  filterTransientCandidates,
  fitTrajectory,
  ransacTrajectory,
  type LandingEstimate,
  type Point,
  type Trajectory,
} from "./ballTracking";

export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  points: Point[],
  curve: Trajectory | null,
  landing?: LandingEstimate | null
) {
  ctx.clearRect(0, 0, w, h);

  if (curve && points.length >= 3) {
    const ts = points.map((p) => p.t);
    const minT = Math.min(...ts);
    const maxT = Math.max(...ts);
    ctx.beginPath();
    for (let i = 0; i <= 60; i++) {
      const t = minT + (maxT - minT) * (i / 60);
      const { x, y } = curve(t);
      const px = x * w;
      const py = y * h;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.strokeStyle = "#c1440e";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.shadowColor = "rgba(0,0,0,0.25)";
    ctx.shadowBlur = 4;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Estimated continuation to landing: visually distinct (dashed, lighter)
    // from the real, detected portion above it, since it's a physics-based
    // guess extending the same curve, not a measurement.
    if (landing) {
      ctx.beginPath();
      ctx.setLineDash([6, 6]);
      for (let i = 0; i <= 30; i++) {
        const t = maxT + (landing.t - maxT) * (i / 30);
        const { x, y } = curve(t);
        const px = x * w;
        const py = y * h;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.strokeStyle = "#c1440e";
      ctx.lineWidth = 2.5;
      ctx.globalAlpha = 0.6;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      ctx.beginPath();
      ctx.arc(landing.x * w, landing.y * h, 5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(193,68,14,0.85)";
      ctx.fill();
      ctx.strokeStyle = "rgba(250,248,242,0.95)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  ctx.fillStyle = "rgba(250,248,242,0.95)";
  ctx.strokeStyle = "rgba(35,82,47,0.9)";
  ctx.lineWidth = 1.5;
  for (const p of points) {
    ctx.beginPath();
    ctx.arc(p.x * w, p.y * h, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - time) < 0.02) {
      resolve();
      return;
    }
    let settled = false;
    function finish() {
      if (settled) return;
      settled = true;
      video.removeEventListener("seeked", onSeeked);
      clearTimeout(timer);
      resolve();
    }
    function onSeeked() {
      finish();
    }
    video.addEventListener("seeked", onSeeked);
    // Safety net: some browsers (notably Safari/iOS) occasionally never
    // fire `seeked` for a given frame — without this a single stuck frame
    // would hang the entire analysis forever.
    const timer = setTimeout(finish, 800);
    video.currentTime = time;
  });
}

export interface AnalysisResult {
  points: Point[];
  curve: Trajectory | null;
  /** True if RANSAC couldn't find a consistent trajectory across frames. */
  unreliable: boolean;
}

/**
 * Samples an uploaded video at regular intervals, runs real object
 * detection (a YOLOv8 model fine-tuned specifically on golf balls) on each
 * sampled frame, then fits the trajectory across the whole video via
 * RANSAC — this is what makes it robust against handheld camera shake and
 * spare balls lying around, since real ball detections line up on one
 * consistent, physically plausible path while incidental clutter doesn't.
 */
export async function processVideoFile(
  video: HTMLVideoElement,
  workCanvas: HTMLCanvasElement,
  onProgress: (fraction: number) => void,
  isCancelled?: () => boolean,
  onStatus?: (status: "loading-model" | "analyzing") => void
): Promise<AnalysisResult> {
  onStatus?.("loading-model");
  const model = await loadBallDetectorModel();
  if (isCancelled?.()) return { points: [], curve: null, unreliable: true };
  onStatus?.("analyzing");

  try {
    await video.play();
    video.pause();
  } catch {
    // Some browsers block programmatic play even muted — seeking usually still works.
  }

  const cropCanvas = makeWorkCanvas();
  const duration = video.duration || 1;
  // A ball is only genuinely visible for a few tenths of a second, so the
  // sampling rate directly determines how many real (not just candidate)
  // frames there are to build a trajectory from — too sparse and RANSAC has
  // only 2-3 real points to work with, which is fragile against even a
  // single bad detection. Modern phone video is commonly 30-60fps, so
  // sampling at up to 45/s gets much closer to "every real frame" than the
  // previous ~22/s cap did, at the cost of a longer analysis.
  const sampleCount = Math.min(400, Math.max(30, Math.round(duration * 45)));
  const candidatesByFrame: Point[][] = [];

  for (let i = 0; i < sampleCount; i++) {
    if (isCancelled?.()) break;
    const t = (i / (sampleCount - 1)) * duration;
    await seekTo(video, t);
    if (isCancelled?.()) break;
    const candidates = await detectBallsTiled(model, video, video.videoWidth, video.videoHeight, workCanvas, cropCanvas);
    candidatesByFrame.push(candidates.map((c) => ({ x: c.x, y: c.y, t })));
    onProgress((i + 1) / sampleCount);
  }

  const transientCandidates = filterTransientCandidates(candidatesByFrame);
  const fit = ransacTrajectory(transientCandidates);
  if (fit) {
    return { points: fit.inliers, curve: fit.curve, unreliable: false };
  }

  // No trajectory cleared the confidence bar — fitting one anyway from
  // *all* raw candidates (never mind that most failed even the basic
  // clutter filter) draws a confident-looking line the UI's "unreliable"
  // caption can't compete with visually. Showing the individual detections
  // with no connecting curve is the honest version of "we don't know":
  // here's what was seen, but no consistent flight path came out of it.
  return { points: transientCandidates.flat(), curve: null, unreliable: true };
}

/** Starts a continuous best-guess detection loop for live visual feedback while recording. */
export function startLiveDetection(
  video: HTMLVideoElement,
  workCanvas: HTMLCanvasElement,
  onPoint: (point: Point) => void,
  onReady?: () => void
): () => void {
  let running = true;
  let predicted: { x: number; y: number } | null = null;
  const lastTwo: Point[] = [];
  const cropCanvas = makeWorkCanvas();

  (async () => {
    const model = await loadBallDetectorModel();
    if (!running) return;
    onReady?.();

    while (running) {
      if (video.videoWidth > 0) {
        const candidates = await detectBallsTiled(model, video, video.videoWidth, video.videoHeight, workCanvas, cropCanvas);
        if (!running) break;
        const best = pickBestCandidate(candidates, predicted);
        if (best) {
          const point = { x: best.x, y: best.y, t: video.currentTime };
          onPoint(point);
          lastTwo.push(point);
          if (lastTwo.length > 2) lastTwo.shift();
          if (lastTwo.length === 2) {
            const dx = lastTwo[1].x - lastTwo[0].x;
            const dy = lastTwo[1].y - lastTwo[0].y;
            predicted = { x: lastTwo[1].x + dx, y: lastTwo[1].y + dy };
          }
        }
      }
      await new Promise((r) => setTimeout(r, 30));
    }
  })();

  return () => {
    running = false;
  };
}

export { extrapolateLanding, fitTrajectory };
export type { LandingEstimate, Point, Trajectory };
